import { ReflectiveDialogs } from "@mcleod/components/src/base/ReflectiveDialogs";
import { Api, ModelRow, ModelRowType, FieldUpdateEvent } from "@mcleod/core";
import { AutogenLayoutEdiChangeField } from "./autogen/AutogenLayoutEdiChangeField";
import { DataSourceMode,  TableRowCreationEvent, TableContentsChangedEvent, ClickEvent, Layout, SlideoutDecorator, TableRowMode, TableAction } from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { EdiChangeFieldAdd } from "./EdiChangeFieldAdd";

export class EdiChangeField extends AutogenLayoutEdiChangeField {

    private _tableName: string;
    private _profileID: string;
    private _isInitialLoad: boolean = true; // Flag to track if we're in initial load phase
    private _isDeletingRow: boolean = false; // Flag to track when we're deleting a row
    public parentNeedsSaving: (needsSaving: boolean) => void;
    public get profileID(): string {
        return this._profileID;
    }
    public set profileID(value: string) {
        this._profileID = value;
    }

    public get tableName(): string {
        return this._tableName;
    }
    public set tableName(value: string) {
        this._tableName = value;
    }

    override async onLoad() {
        // Ensure the data source is in a mode that allows adding new rows
        if (this.sourceEdiChangeField) {
            this.sourceEdiChangeField.mode = DataSourceMode.NONE;

            // Add a before execution listener to mark when we start loading data
            this.sourceEdiChangeField.addBeforeExecutionListener(() => {
                this._isInitialLoad = true;
            });

            // Add a listener to ensure the data source stays in the right mode for adding
            this.sourceEdiChangeField.addAfterExecutionListener(() => {
                // After data is loaded, keep the mode that allows adding
                if (this.sourceEdiChangeField.mode !== DataSourceMode.ADD) {
                    this.sourceEdiChangeField.mode = DataSourceMode.NONE;
                }
                // Add field update listeners to all loaded rows, but don't trigger save notification yet
                this.addFieldUpdateListenersToAllRowsQuietly();

                // Set a timeout to mark that initial loading is complete
                // This allows time for any data binding/display operations to complete
                setTimeout(() => {
                    this._isInitialLoad = false;
                }, 100);
            });
        }
        this.tableChangeField.addRowCreateListener((event) => {
            // This fires when a new row is being added to the table
            const tableRow = event.getTableRow();
            // Override the deleteRow method for this specific row
            tableRow.deleteRow = (afterDelete?: () => {}) => {
                ReflectiveDialogs.showYesNo("Are you sure you want to delete this record?", "Confirm Deletion").then(async (clickedYes) => {
                    if (clickedYes) {
                        try {
                            // Set flag to indicate we're performing a delete operation
                            this._isDeletingRow = true;

                            // Custom delete logic - call the API to delete the record
                            await Api.delete("lme/datafusion/edi-change-field", { id: tableRow.data.get("id") });

                            // Manually remove the row from the table without triggering events
                            this.tableChangeField.removeRow(tableRow.index);

                            // Call the afterDelete callback if provided
                            if (afterDelete) {
                                afterDelete();
                            }

                            // Reset the flag after deletion is complete, with a small delay to ensure all events are processed
                            setTimeout(() => {
                                this._isDeletingRow = false;
                            }, 10);
                        } catch (error) {
                           CommonDialogs.showError("Error deleting record: " + error);
                            // Reset the flag on error as well
                            this._isDeletingRow = false;
                            // Handle error as needed
                        }
                    }
                });
            };
        });

    }

/** This is an event handler for the onRowCreate event of tableChangeField. */
  tableChangeFieldOnRowCreate(event: TableRowCreationEvent) {
    const row = event.getTableRow();
    row.data.set("table_name", this.tableName);
    row.data.set("profile_id", this.profileID);
    row.data.set("direction", "O");
    // Add field update listener to the new row
    this.addFieldUpdateListenerToRow(row.data);
  }

/** This is an event handler for the onContentsChanged event of tableChangeField. */
  tableChangeFieldOnContentsChanged(event: TableContentsChangedEvent) {
    // Only notify parent if this is not a deletion operation
    // Deletions are committed immediately via API and don't need to be saved via the parent
    if (!this._isDeletingRow) {
      this.parentNeedsSaving(true);
    }
  }

/** This is an event handler for the onClick event of buttonChangeFields. */
  buttonChangeFieldsOnClick(event: ClickEvent) {
    const layoutName = "lme/datafusion/EdiChangeFieldAdd";
    const layout = Layout.getLayout(layoutName,{width:  window.innerWidth * .80}) as EdiChangeFieldAdd
    const sod = new SlideoutDecorator({
        layout: layout,
        title: "EDI Change Fields - " + this.tableName,
        fillVerticalSpace: true,
        addlComponents: [layout.saveButton],
        doAfterSlideIn: async (decorator) => {
            await layout.sourceEdiChangeField.search({table_name: this.tableName, profile_id: this.profileID, fields_only: true, tables_only: false });
            // Search to load fields for the specified table
            await layout.sourceFieldinfoNoLookupJoin.search({table_name: this.tableName});
            layout.selectMatchingFields();
             layout.saveButton.addClickListener(async event => {
                layout.saveButton.busy = true;
                await this.handleSaveButtonClick(layout, decorator);
                layout.saveButton.busy = false;
             });
        }
    });

  }

  /** Handle the save button click logic */
  private async handleSaveButtonClick(layout: EdiChangeFieldAdd, decorator: SlideoutDecorator) {
    const selectedRows = layout.tableChangeFieldInfo.selectedRows;

    // First, check for rows that will be deleted and get confirmation
    const selectedFieldNames = new Set(
        selectedRows.map(row => row.data.get("field_name"))
    );

    // Collect rows that will be deleted
    const rowsToDelete = [];

    // Get all current rows from the table (work backwards to avoid index issues)
    const currentRows = [...this.tableChangeField.allRows];
    for (let i = currentRows.length - 1; i >= 0; i--) {
        const currentRow = currentRows[i];
        const currentFieldName = currentRow.data.get("field_name");

        // If this field is not in the selected rows, collect it for deletion
        if (!selectedFieldNames.has(currentFieldName)) {
            rowsToDelete.push({
                index: i,
                tableRow: currentRow,
                modelRow: currentRow.data
            });
        }
    }

    // If there are rows to delete, ask for confirmation
    if (rowsToDelete.length > 0) {
        const confirmDelete = await ReflectiveDialogs.showYesNo(
            `${rowsToDelete.length} rows will be deleted, do you wish to continue?`,
            "Confirm Deletion"
        );

        if (!confirmDelete) {
            return; // User cancelled, don't proceed with any changes
        }

        // Proceed with deletion
        this._isDeletingRow = true; // Set flag once before the loop

        for (const rowToDelete of rowsToDelete) {
            // Attempt to perform a delete operation on the modelRow
            try {
                if (rowToDelete.modelRow.get("id")) {
                    await rowToDelete.modelRow.delete();
                }
            } catch (error) {
                console.error("Error deleting row from database:", error);
            }

            this.tableChangeField.removeRow(rowToDelete.index);
        }

        // Reset the flag after all deletions are complete, with a small delay to ensure all events are processed
        setTimeout(() => {
            this._isDeletingRow = false;
        }, 10);
    }

    // Get existing field names from sourceEdiChangeField to avoid duplicates
    const existingFieldNames = new Set(
        this.sourceEdiChangeField.data.map(row => row.get("field_name"))
    );

    // Process selected rows and add missing ones
    for (const selectedRow of selectedRows) {
        const fieldName = selectedRow.data.get("field_name");
        const caption = selectedRow.data.get("caption");

        // Only add if not already present in sourceEdiChangeField
        if (!existingFieldNames.has(fieldName)) {
            // Create a new row data instance for the table
            const newRowData = await this.tableChangeField._createNewRowData();

            // Set the initial data
            newRowData.set("field_name", fieldName);
            newRowData.set("caption", caption);
            // Create lookup data for the field_name to display properly in the textbox
            const lookupData = new ModelRow("lme/datafusion/fieldinfo-no-lookup-join");
            lookupData.set("field_name", fieldName);
            lookupData.type = ModelRowType.LOOKUP_MODEL_DATA;
            // Set the lookup model data for the field_name field
            newRowData.setLookupModelData("field_name", lookupData);

            newRowData.set("table_name", this.tableName);
            newRowData.set("profile_id", this.profileID);                        // Set all checkboxes to true (add, update, delete)
            newRowData.set("send_for_add", true);
            newRowData.set("send_for_update", true);
            newRowData.set("send_for_delete", true);
            newRowData.set("direction", "O");

            // Add field update listener to the new row before adding it to the table
            this.addFieldUpdateListenerToRow(newRowData);

            // Add the row to the table
            const addRowResult = this.tableChangeField.addRow(newRowData, { mode: TableRowMode.ADD }, { display: true, addToData: true });

            // Force the row to display its data properly
            if (addRowResult.row) {
                addRowResult.row.displayData(newRowData, this.sourceEdiChangeField.data, addRowResult.row.index);
            }
        }
    }

    // Mark parent as needing saving
    this.parentNeedsSaving(true);

    // Close the slideout
    decorator.slideOut();
  }

  /**
   * Adds field update listeners to all rows without triggering save notification
   * Used when data is initially loaded
   */
  private addFieldUpdateListenersToAllRowsQuietly(): void {
    if (this.sourceEdiChangeField && this.sourceEdiChangeField.data) {
      this.sourceEdiChangeField.data.forEach(row => {
        this.addFieldUpdateListenerToRowQuietly(row);
      });
    }
  }

  /**
   * Adds a field update listener to a specific row
   * @param row The ModelRow to add the listener to
   */
  private addFieldUpdateListenerToRow(row: ModelRow): void {
    // Check if we've already added listeners to avoid duplicates
    if ((row as any)._fieldUpdateListenerAdded) {
      return;
    }

    // Add the actual after field update listener
    row.addAfterFieldUpdateListener((event: FieldUpdateEvent) => {
      // Notify parent that changes have been made
      if (this.parentNeedsSaving) {
        this.parentNeedsSaving(true);
      }
    });

    // Mark that we've added a listener to this row
    (row as any)._fieldUpdateListenerAdded = true;
  }

  /**
   * Adds a field update listener to a specific row without triggering save notification immediately
   * Used for rows loaded from existing data
   * @param row The ModelRow to add the listener to
   */
  private addFieldUpdateListenerToRowQuietly(row: ModelRow): void {
    // Check if we've already added listeners to avoid duplicates
    if ((row as any)._fieldUpdateListenerAdded) {
      return;
    }

    // Add the actual after field update listener - this will only trigger on actual changes
    row.addAfterFieldUpdateListener((event: FieldUpdateEvent) => {
      // Only notify parent if we're not in initial load phase
      if (!this._isInitialLoad && this.parentNeedsSaving) {
        this.parentNeedsSaving(true);
      }
    });

    // Mark that we've added a listener to this row
    (row as any)._fieldUpdateListenerAdded = true;
  }
}
